import React, { useState } from "react";
import { KeyRound, Check, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

/**
 * Admin self-service change-password page.
 * Endpoint: POST /api/admin/auth/change-password
 * Only allows the currently authenticated admin to change their OWN password.
 */
export default function AdminChangePassword() {
  const { user, refresh } = useAuth();
  const [currentPassword, setCurrent] = useState("");
  const [newPassword, setNew] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const rules = [
    { key: "len",     label: "At least 12 characters",   ok: newPassword.length >= 12 },
    { key: "upper",   label: "One uppercase letter",     ok: /[A-Z]/.test(newPassword) },
    { key: "lower",   label: "One lowercase letter",     ok: /[a-z]/.test(newPassword) },
    { key: "digit",   label: "One number",               ok: /[0-9]/.test(newPassword) },
    { key: "special", label: "One special character",    ok: /[!@#$%^&*()\-_=+[\]{};:'",.<>/?\\|`~]/.test(newPassword) },
  ];
  const allOk = rules.every(r => r.ok);
  const matches = newPassword.length > 0 && newPassword === confirm;

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!allOk) { setError("New password does not meet the requirements"); return; }
    if (!matches) { setError("New password and confirm password do not match"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
        confirm_new_password: confirm,
      });
      if (data?.access_token) {
        try { localStorage.setItem("admin_access_token", data.access_token); } catch {}
      }
      await refresh();
      toast.success("Password changed");
      setCurrent(""); setNew(""); setConfirm("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Could not change password";
      setError(msg);
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl" data-testid="admin-change-password">
      <div className="flex items-center gap-2 mb-1">
        <KeyRound className="w-5 h-5 text-amber-400" />
        <h1 className="text-xl font-medium">Change password</h1>
      </div>
      <p className="text-sm text-stone-400 mb-6">
        Signed in as <span className="text-stone-200">{user?.email || user?.username}</span>. You can only change your own password from here.
      </p>

      <form onSubmit={submit} className="bg-stone-950 border border-stone-800 rounded-2xl p-6 space-y-5">
        <div>
          <label className="text-[11px] uppercase tracking-[0.18em] text-stone-400">Current password</label>
          <input
            type="password" required autoComplete="current-password"
            value={currentPassword} onChange={(e) => setCurrent(e.target.value)}
            className="mt-1.5 w-full h-11 px-3 rounded-lg bg-stone-900/60 border border-stone-700 text-stone-100 text-sm focus:border-amber-500/60 outline-none"
            data-testid="adm-cp-current"
          />
        </div>

        <div>
          <label className="text-[11px] uppercase tracking-[0.18em] text-stone-400">New password</label>
          <input
            type="password" required autoComplete="new-password"
            value={newPassword} onChange={(e) => setNew(e.target.value)}
            className="mt-1.5 w-full h-11 px-3 rounded-lg bg-stone-900/60 border border-stone-700 text-stone-100 text-sm focus:border-amber-500/60 outline-none"
            data-testid="adm-cp-new"
          />
          <ul className="mt-2 space-y-1">
            {rules.map(r => (
              <li key={r.key} className={`text-[11px] flex items-center gap-1.5 ${r.ok ? "text-emerald-400" : "text-stone-500"}`}>
                <Check className={`w-3 h-3 ${r.ok ? "opacity-100" : "opacity-30"}`} /> {r.label}
              </li>
            ))}
          </ul>
        </div>

        <div>
          <label className="text-[11px] uppercase tracking-[0.18em] text-stone-400">Confirm new password</label>
          <input
            type="password" required autoComplete="new-password"
            value={confirm} onChange={(e) => setConfirm(e.target.value)}
            className={`mt-1.5 w-full h-11 px-3 rounded-lg bg-stone-900/60 border text-stone-100 text-sm outline-none ${matches || confirm.length === 0 ? "border-stone-700 focus:border-amber-500/60" : "border-red-700 focus:border-red-500"}`}
            data-testid="adm-cp-confirm"
          />
          {confirm.length > 0 && !matches && (
            <p className="mt-1 text-[11px] text-red-400">Passwords do not match</p>
          )}
        </div>

        {error && (
          <div className="flex items-start gap-2 text-xs text-red-400 border border-red-500/30 bg-red-500/5 rounded-md p-2" data-testid="adm-cp-error">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={busy || !allOk || !matches || !currentPassword}
          className="h-11 px-6 rounded-lg bg-amber-500 text-stone-950 font-semibold hover:bg-amber-400 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="adm-cp-submit"
        >{busy ? "Changing…" : "Change password"}</button>
      </form>
    </div>
  );
}
